import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PrimaryNavigation } from '../../components/navigation/PrimaryNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import FinancialSummaryCard from './components/FinancialSummaryCard';
import RetirementCalculator from './components/RetirementCalculator';
import ProjectionPanel from './components/ProjectionPanel';
import EducationalContent from './components/EducationalContent';
import TimelineSlider from './components/TimelineSlider';
import { generateRetirementPDF } from '../../utils/pdfExport';

const RetirementPlanningSetup = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [projectionData, setProjectionData] = useState(null);
  const [retirementAge, setRetirementAge] = useState(60);

  // Fetch user data from localStorage instead of hardcoding
  const getUserDataFromStorage = () => {
    try {
      const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      const retirementPlan = JSON.parse(localStorage.getItem('retirementPlan') || '{}');
      
      // Calculate current age from date of birth
      const calculateCurrentAge = (dobString) => {
        if (!dobString) return 32; // Default fallback
        
        const [day, month, year] = dobString?.split('/')?.map(Number);
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today?.getFullYear() - birthDate?.getFullYear();
        const monthDiff = today?.getMonth() - birthDate?.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
          age--;
        }
        
        return age;
      };

      const currentAge = userProfile?.dateOfBirth ? calculateCurrentAge(userProfile?.dateOfBirth) : 32;

      return {
        name: userProfile?.fullName || 'User',
        currentAge: currentAge,
        monthlyIncome: 85000, // This should ideally come from form or be editable
        preRetirementInflation: 6.5,
        postRetirementInflation: 5.5,
        retirementAge: retirementPlan?.retirementAge || Math.max(60, currentAge + 1), // Ensure it's at least currentAge + 1
        fullName: userProfile?.fullName || userProfile?.name || '-',
        email: userProfile?.email || '-',
        dateOfBirth: userProfile?.dateOfBirth || '-'
      };
    } catch (error) {
      console.error('Error fetching user data:', error);
      return {
        name: 'User',
        currentAge: 32,
        monthlyIncome: 85000,
        preRetirementInflation: 6.5,
        postRetirementInflation: 5.5,
        retirementAge: 60,
        fullName: '-',
        email: '-',
        dateOfBirth: '-'
      };
    }
  };

  // Initialize userData from localStorage
  const [userData, setUserData] = useState(getUserDataFromStorage());

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Sync retirementAge state with userData
    setRetirementAge(userData?.retirementAge);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [userData?.retirementAge]);

  const formatIndianCurrency = (amount) => {
    return '₹' + amount?.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  // Add handler for editing user data
  const handleEditUserData = (field, value) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));

    // If retirement age is being updated, save to localStorage and update state
    if (field === 'retirementAge') {
      try {
        const retirementPlan = JSON.parse(localStorage.getItem('retirementPlan') || '{}');
        retirementPlan.retirementAge = value;
        localStorage.setItem('retirementPlan', JSON.stringify(retirementPlan));
        setRetirementAge(value);
      } catch (error) {
        console.error('Error saving retirement age:', error);
      }
    }

    // Recalculate projections if they exist
    if (projectionData) {
      setProjectionData(null);
    }
  };

  const handleCalculate = (data) => {
    const projectionDataWithIncome = {
      ...data,
      totalCorpusRequired: data?.totalCorpusRequired ?? data?.corpusNeeded,
      requiredMonthlyIncome: data?.requiredMonthlyIncome ?? data?.futureMonthlyIncome,
      currentIncome: userData?.monthlyIncome
    };
    
    setProjectionData(projectionDataWithIncome);
    
    // Save retirement projection to localStorage for dashboard and life goals page
    try {
      localStorage.setItem('retirementProjection', JSON.stringify(projectionDataWithIncome));
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('retirementDataUpdated', {
        detail: projectionDataWithIncome
      }));
    } catch (error) {
      console.error('Error saving retirement projection:', error);
    }
  };

  const handleProceedToLifeGoals = () => {
    if (!projectionData) {
      alert('Please calculate your retirement plan before proceeding');
      return;
    }
    navigate('/life-goals-management');
  };

  const handleExportPDF = async () => {
    if (!projectionData) {
      alert('Please calculate your retirement plan before exporting');
      return;
    }

    // Ask user for export preference
    const useEnhanced = window.confirm(
      'Would you like to export the PDF with our custom report design?\n\n' +
      'Click "OK" for the custom formatted PDF (recommended)\n' +
      'Click "Cancel" to export the exact web page layout (larger file size)'
    );

    const result = await generateRetirementPDF(userData, projectionData, retirementAge, useEnhanced);
    
    if (result?.success) {
      alert(`PDF exported successfully: ${result?.fileName}`);
    } else {
      alert(`Failed to export PDF: ${result?.error || 'Unknown error'}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <main className={`retirement-planning-page main-content ${isMobile ? 'main-content-mobile' : 'main-content-desktop'}`}>
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
              <Icon name="PiggyBank" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground">Retirement Planning Setup</h1>
              <p className="text-muted-foreground mt-1">Define your retirement income requirements with inflation-adjusted calculations</p>
            </div>
            {projectionData && (
              <Button
                variant="outline"
                iconName="FileText"
                iconPosition="left"
                onClick={handleExportPDF}
              >
                Export PDF
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <Icon name="User" size={20} className="text-primary" />
            <p className="text-sm text-foreground">
              Welcome back, <span className="font-semibold">{userData?.name}</span>! Let's plan your retirement journey.
            </p>
          </div>
        </div>

        {/* Financial Summary Cards - Updated with edit functionality */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <FinancialSummaryCard
            icon="Calendar"
            label="Current Age"
            value={`${userData?.currentAge} years`}
            description="Your current age"
            variant="default"
            isEditable={true}
            editField="currentAge"
            currentValue={userData?.currentAge}
            onEdit={handleEditUserData}
            inputType="number"
            suffix="years"
          />
          <FinancialSummaryCard
            icon="Wallet"
            label="Current Monthly Income"
            value={formatIndianCurrency(userData?.monthlyIncome)}
            description="Your take-home salary"
            variant="primary"
            isEditable={true}
            editField="monthlyIncome"
            currentValue={userData?.monthlyIncome}
            onEdit={handleEditUserData}
            inputType="number"
            prefix="₹"
          />
          <FinancialSummaryCard
            icon="Target"
            label="Expected Retirement Age"
            value={`${userData?.retirementAge} years`}
            description="Your planned retirement age"
            variant="accent"
            isEditable={true}
            editField="retirementAge"
            currentValue={userData?.retirementAge}
            onEdit={handleEditUserData}
            inputType="number"
            suffix="years"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <FinancialSummaryCard
            icon="TrendingUp"
            label="Pre-Retirement Inflation"
            value={`${userData?.preRetirementInflation}%`}
            description="Expected lifestyle inflation rate"
            variant="default"
            isEditable={true}
            editField="preRetirementInflation"
            currentValue={userData?.preRetirementInflation}
            onEdit={handleEditUserData}
            inputType="number"
            suffix="%"
          />
          <FinancialSummaryCard
            icon="TrendingDown"
            label="Post-Retirement Inflation"
            value={`${userData?.postRetirementInflation}%`}
            description="Expected inflation after retirement"
            variant="default"
            isEditable={true}
            editField="postRetirementInflation"
            currentValue={userData?.postRetirementInflation}
            onEdit={handleEditUserData}
            inputType="number"
            suffix="%"
          />
          <div className="flex items-center p-4 bg-muted/30 border border-border rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="Info" size={20} className="text-primary" />
              <p className="text-sm text-muted-foreground">
                Click the <Icon name="Edit2" size={14} className="inline text-primary mx-1" /> icon on any card to update your financial parameters. Changes will reset your retirement calculations.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Left Column */}
          <div className="space-y-6">
            <TimelineSlider
              currentAge={userData?.currentAge}
              retirementAge={retirementAge}
              onRetirementAgeChange={setRetirementAge}
            />
            
            <RetirementCalculator
              currentAge={userData?.currentAge}
              retirementAge={retirementAge}
              monthlyIncome={userData?.monthlyIncome}
              preRetirementInflation={userData?.preRetirementInflation}
              postRetirementInflation={userData?.postRetirementInflation}
              onCalculate={handleCalculate}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <ProjectionPanel projectionData={projectionData} />
            
            {projectionData && (
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="CheckCircle2" size={20} className="text-success" />
                  <h3 className="text-lg font-semibold text-foreground">Next Steps</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Your retirement plan is ready! Now let's set up your life goals to create a comprehensive financial roadmap.
                </p>
                <Button
                  variant="default"
                  fullWidth
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={handleProceedToLifeGoals}
                >
                  Proceed to Life Goals
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Educational Content */}
        <div className="mb-8">
          <EducationalContent />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center p-6 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="Shield" size={20} className="text-primary" />
            <p className="text-sm text-muted-foreground">
              Your data is secure and stored locally
            </p>
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => navigate('/user-registration')}
              iconName="ArrowLeft"
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Back
            </Button>
            <Button
              variant="default"
              onClick={handleProceedToLifeGoals}
              iconName="ArrowRight"
              iconPosition="right"
              disabled={!projectionData}
              className="flex-1 sm:flex-none"
            >
              Continue
            </Button>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-8 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Important Considerations</h4>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>• All calculations are estimates based on assumed rates of return and inflation</li>
                <li>• Actual returns may vary based on market conditions and investment choices</li>
                <li>• Consider consulting a certified financial planner for personalized advice</li>
                <li>• Review and adjust your plan annually to stay on track</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RetirementPlanningSetup;